<template>
  <div class="min-h-screen bg-gray-50">
    <!-- ヘッダー -->
    <header class="bg-white shadow">
      <div class="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div class="flex justify-between h-16">
          <div class="flex items-center">
            <router-link to="/dashboard" class="text-2xl font-bold text-gray-900">
              Soarimetric
            </router-link>
          </div>
          <nav class="flex items-center space-x-4">
            <router-link to="/dashboard" class="text-gray-500 hover:text-gray-700 px-3 py-2">
              ダッシュボード
            </router-link>
          </nav>
        </div>
      </div>
    </header>

    <main class="max-w-4xl mx-auto py-6 sm:px-6 lg:px-8">
      <div class="px-4 py-6 sm:px-0">
        <h1 class="text-3xl font-bold text-gray-900 mb-8">アカウント設定</h1>

        <!-- ユーザー情報 -->
        <div class="bg-white shadow rounded-lg mb-6">
          <div class="px-4 py-5 sm:p-6">
            <h2 class="text-lg font-medium text-gray-900 mb-4">基本情報</h2>
            
            <div class="space-y-4">
              <div>
                <label class="block text-sm font-medium text-gray-700">
                  メールアドレス
                </label>
                <div class="mt-1 text-sm text-gray-900">
                  {{ user?.email }}
                </div>
              </div>

              <div>
                <label for="sport-type" class="block text-sm font-medium text-gray-700">
                  スポーツタイプ
                </label>
                <div class="mt-1 flex items-center space-x-3">
                  <select
                    v-model="selectedSportType"
                    id="sport-type"
                    class="block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500 sm:text-sm"
                    :disabled="isUpdatingSport"
                  >
                    <option value="">スポーツタイプを選択</option>
                    <option value="パラグライダー">パラグライダー</option>
                    <option value="ハンググライダー">ハンググライダー</option>
                    <option value="グライダー">グライダー</option>
                    <option value="気球">気球</option>
                  </select>
                  <button
                    @click="updateSportType"
                    :disabled="isUpdatingSport || selectedSportType === user?.sport_type"
                    class="bg-blue-600 hover:bg-blue-700 text-white font-medium py-2 px-4 rounded text-sm disabled:opacity-50"
                  >
                    {{ isUpdatingSport ? '更新中...' : '更新' }}
                  </button>
                </div>
                <p class="mt-1 text-sm text-gray-500">
                  現在: {{ user?.sport_type || '未設定' }}
                </p>
              </div>
              
              <div>
                <label class="block text-sm font-medium text-gray-700">
                  登録日
                </label>
                <div class="mt-1 text-sm text-gray-900">
                  {{ formatDate(user?.date_joined) }}
                </div>
              </div>
            </div>
          </div>
        </div>

        <!-- サブスクリプション情報 -->
        <div class="bg-white shadow rounded-lg mb-6">
          <div class="px-4 py-5 sm:p-6">
            <h2 class="text-lg font-medium text-gray-900 mb-4">サブスクリプション</h2>
            
            <div class="space-y-4">
              <div>
                <label class="block text-sm font-medium text-gray-700">
                  現在のプラン
                </label>
                <div class="mt-1 flex items-center">
                  <span class="text-lg font-semibold text-gray-900 mr-2">
                    {{ subscription?.plan === 'premium' ? 'プレミアム' : '無料' }}
                  </span>
                  <span 
                    v-if="subscription?.plan === 'premium'"
                    :class="getStatusColor(subscription?.status)"
                    class="px-2 py-1 text-xs font-medium rounded-full"
                  >
                    {{ getStatusText(subscription?.status) }}
                  </span>
                </div>
              </div>
              
              <div v-if="subscription?.plan === 'premium'">
                <label class="block text-sm font-medium text-gray-700">
                  次回更新日
                </label>
                <div class="mt-1 text-sm text-gray-900">
                  {{ formatDate(subscription?.current_period_end) }}
                </div>
              </div>

              <div class="pt-4">
                <div v-if="subscription?.plan === 'free'" class="space-y-3">
                  <router-link
                    to="/pricing"
                    class="inline-block bg-blue-600 hover:bg-blue-700 text-white font-bold py-2 px-4 rounded"
                  >
                    プレミアムにアップグレード
                  </router-link>
                </div>
                
                <div v-else class="space-y-3">
                  <button
                    @click="openPortal"
                    :disabled="isLoading"
                    class="bg-gray-600 hover:bg-gray-700 text-white font-bold py-2 px-4 rounded mr-4 disabled:opacity-50"
                  >
                    {{ isLoading ? '処理中...' : '請求情報を管理' }}
                  </button>
                  
                  <button
                    @click="cancelSubscription"
                    :disabled="isLoading || subscription?.status === 'canceled'"
                    class="bg-red-600 hover:bg-red-700 text-white font-bold py-2 px-4 rounded disabled:opacity-50"
                  >
                    {{ isLoading ? '処理中...' : 'サブスクリプションを解約' }}
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>

        <!-- 危険な操作 -->
        <div class="bg-white shadow rounded-lg">
          <div class="px-4 py-5 sm:p-6">
            <h2 class="text-lg font-medium text-red-600 mb-4">危険な操作</h2>
            
            <div class="space-y-4">
              <div>
                <h3 class="text-sm font-medium text-gray-900 mb-2">
                  アカウントの削除
                </h3>
                <p class="text-sm text-gray-600 mb-4">
                  アカウントを削除すると、すべてのデータが永続的に削除されます。この操作は取り消せません。
                </p>
                <button
                  @click="confirmAccountDeletion"
                  class="bg-red-600 hover:bg-red-700 text-white font-bold py-2 px-4 rounded"
                >
                  アカウントを削除
                </button>
              </div>
            </div>
          </div>
        </div>

        <div v-if="errorMessage" class="mt-6 bg-red-50 border border-red-200 rounded-lg p-4">
          <p class="text-red-800">{{ errorMessage }}</p>
        </div>

        <div v-if="successMessage" class="mt-6 bg-green-50 border border-green-200 rounded-lg p-4">
          <p class="text-green-800">{{ successMessage }}</p>
        </div>
      </div>
    </main>
  </div>
</template>

<script>
import { ref, onMounted } from 'vue'
import { useRouter } from 'vue-router'
import { useAuthStore } from '@/stores/auth'
import axios from 'axios'

const API_BASE_URL = process.env.VUE_APP_API_URL || ''

export default {
  name: 'Account',
  setup() {
    const router = useRouter()
    const authStore = useAuthStore()
    
    const user = ref(null)
    const subscription = ref(null)
    const isLoading = ref(false)
    const errorMessage = ref('')
    const successMessage = ref('')
    const selectedSportType = ref('')
    const isUpdatingSport = ref(false)

    const loadAccountData = async () => {
      try {
        // MVP版では支払い機能は削除されているため、プロファイルのみを読み込み
        const userResponse = await axios.get(`${API_BASE_URL}/api/auth/profile/`)
        
        user.value = userResponse.data
        selectedSportType.value = userResponse.data.sport_type || ''
        
        // MVP版: 全て無料プランとして扱う
        subscription.value = {
          plan: 'free',
          status: 'active'
        }
        
        console.log('User data loaded:', userResponse.data)
        console.log('Sport type:', userResponse.data.sport_type)
      } catch (error) {
        console.error('Account data loading failed:', error)
        errorMessage.value = 'アカウント情報の読み込みに失敗しました'
      }
    }

    const openPortal = async () => {
      isLoading.value = true
      errorMessage.value = ''

      try {
        const response = await axios.post(`${API_BASE_URL}/api/payments/portal/`)
        
        if (response.data.portal_url) {
          window.location.href = response.data.portal_url
        } else {
          errorMessage.value = 'ポータルURLが取得できませんでした'
        }
      } catch (error) {
        errorMessage.value = error.response?.data?.error || 'ポータルの開始に失敗しました'
      } finally {
        isLoading.value = false
      }
    }

    const cancelSubscription = async () => {
      if (!confirm('本当にサブスクリプションを解約しますか？')) {
        return
      }

      isLoading.value = true
      errorMessage.value = ''
      successMessage.value = ''

      try {
        const response = await axios.post(`${API_BASE_URL}/api/payments/cancel/`)
        
        subscription.value = response.data.subscription
        successMessage.value = 'サブスクリプションが解約されました。次回更新日まで引き続きプレミアム機能をご利用いただけます。'
      } catch (error) {
        errorMessage.value = error.response?.data?.error || 'サブスクリプションの解約に失敗しました'
      } finally {
        isLoading.value = false
      }
    }

    const confirmAccountDeletion = () => {
      const confirmation = prompt(
        'アカウントを削除するには「削除」と入力してください。この操作は取り消せません。'
      )
      
      if (confirmation === '削除') {
        deleteAccount()
      }
    }

    const updateSportType = async () => {
      if (!selectedSportType.value || selectedSportType.value === user.value?.sport_type) {
        return
      }

      isUpdatingSport.value = true
      errorMessage.value = ''
      successMessage.value = ''

      try {
        const response = await axios.patch(`${API_BASE_URL}/api/auth/profile/`, {
          sport_type: selectedSportType.value
        })
        
        user.value = response.data
        successMessage.value = 'スポーツタイプを更新しました'
        
        // 更新が成功したら認証ストアも更新
        authStore.user = response.data
      } catch (error) {
        console.error('Sport type update failed:', error)
        errorMessage.value = error.response?.data?.error || 'スポーツタイプの更新に失敗しました'
        // エラーが発生した場合は元の値に戻す
        selectedSportType.value = user.value?.sport_type || ''
      } finally {
        isUpdatingSport.value = false
      }
    }

    const deleteAccount = async () => {
      isLoading.value = true
      errorMessage.value = ''

      try {
        await axios.delete(`${API_BASE_URL}/api/auth/profile/`)
        
        await authStore.logout()
        router.push('/')
      } catch (error) {
        errorMessage.value = error.response?.data?.error || 'アカウントの削除に失敗しました'
      } finally {
        isLoading.value = false
      }
    }

    const formatDate = (dateString) => {
      if (!dateString) return '-'
      
      const date = new Date(dateString)
      return date.toLocaleDateString('ja-JP', {
        year: 'numeric',
        month: 'long',
        day: 'numeric'
      })
    }

    const getStatusColor = (status) => {
      switch (status) {
        case 'active': return 'bg-green-100 text-green-800'
        case 'canceled': return 'bg-red-100 text-red-800'
        case 'past_due': return 'bg-yellow-100 text-yellow-800'
        default: return 'bg-gray-100 text-gray-800'
      }
    }

    const getStatusText = (status) => {
      switch (status) {
        case 'active': return 'アクティブ'
        case 'canceled': return '解約済み'
        case 'past_due': return '支払い遅延'
        case 'unpaid': return '未払い'
        default: return status
      }
    }

    onMounted(() => {
      loadAccountData()
    })

    return {
      user,
      subscription,
      isLoading,
      errorMessage,
      successMessage,
      selectedSportType,
      isUpdatingSport,
      openPortal,
      cancelSubscription,
      updateSportType,
      confirmAccountDeletion,
      formatDate,
      getStatusColor,
      getStatusText
    }
  }
}
</script>
